import { defineComponent as _defineComponent } from 'vue'
import Vue, { computed, ref } from 'vue';

import type { TitleDetail } from '@/interfaces/title-details-graphql';

import { ThumborOptions } from '@/filters/thumbor';

import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';

import { mrcObservableOptions } from '@/helpers/composables/useBatchImpressionTracking';

import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import { useI18n } from '@/helpers/composables/useI18n';
import { useQualityTV } from '@/features/buybox/composables/useQualityTv';
import { Offer } from '@/@types/graphql-types';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { UsePromotion } from '@/helpers/composables/usePromotion';
import { qualityTVProviderConfig } from '@/features/buybox/helpers/quality-tv-data';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { faPlay } from '@fortawesome/free-solid-svg-icons';


export default /*#__PURE__*/_defineComponent({
  __name: 'PPExpQTV',
  props: {
    catalogueItem: null,
    title: null,
    offers: null,
    additionalContexts: null,
    seasonNumber: null,
    episodeNumber: null,
    qualityProviderId: null
  },
  setup(__props: any) {

const props = __props as {
	catalogueItem: UsePromotion['catalogueItem'];
	title: TitleDetail;
	offers: Offer[];
	additionalContexts: SnowplowContext[];
	seasonNumber?: number | null;
	episodeNumber?: number | null;
	qualityProviderId?: number;
};



const titleListContainer = ref<HTMLElement | null>(null);

const { tc, t } = useI18n();
const { country } = useLanguageStore();

const qualityTVProviderId = computed(() => {
	// check if we don't have an overwrite from yield optimisation
	if (!!props.catalogueItem?.placement) {
		return props.catalogueItem.packageId;
	}

	return props.qualityProviderId || 350;
});

const qualityTVType = computed(() => {
	return qualityTVProviderConfig[qualityTVProviderId.value]?.type;
});

const isFreeTrialQTV = computed(() => qualityTVType.value === 'FREE');

const bannerText = computed(() => {
	if (country.value === 'ES' && props.catalogueItem?.shortName === 'sst') return 'Ve a SkyShowtime ahora';

	if (qualityTVType.value === 'SVOD')
		return t('WEBAPP_PROVIDER_QUALITY_TV_STREAM', { provider: qualityTVProvider.value.clearName });
	if (qualityTVType.value === 'TVOD')
		return t('WEBAPP_PROVIDER_QUALITY_TV_RENT_OR_BUY', { provider: qualityTVProvider.value.clearName });
	return t('WEBAPP_PROVIDER_QUALITY_TV_BANNER', { provider: qualityTVProvider.value.clearName });
});

const titlesShownCount = computed(() => {
	const containerWidth = titleListContainer.value?.clientWidth || 0;
	const posterWidth = 65;
	const gap = 0;
	// in order to count the number visible in the viewport we divide the width of the container by the width of the poster + gap
	return Math.round(containerWidth / (posterWidth + gap));
});

const { handleTitleClick, handleFreeTrialClick, onVisibilityChanged, qualityTVTitles } = useQualityTV({
	packageId: qualityTVProviderId.value,
	sourceTitle: () => props.title,
	titlesShownCount: titlesShownCount,
	trackingContexts: () => props.trackingContexts,
	additionalContexts: () => props.additionalContexts,
	seasonNumber: props.seasonNumber,
	episodeNumber: props.episodeNumber,
	titleCount: 10,
});

const qualityTVProvider = computed(() => {
	// check if we don't have an overwrite from yield optimisation
	if (!!props.catalogueItem?.placement) {
		return props.catalogueItem;
	}

	return qualityTVProviderConfig[qualityTVProviderId.value];
});

const freeTrialQTVCTA = computed(() => {
	if (['AU', 'CA'].includes(country.value)) {
		return tc('WEBAPP_WATCHNOW');
	}

	return tc('WEBAPP_QUALITY_TV_CTA');
});

const hideTitlesFade = ref(false);

const intersectionOptionsForFade = {
	once: false,
	throttle: 0,
	intersection: {
		root: null,
		rootMargin: '0px 0px 0px 0px',
		threshold: 0.95,
	},
};

function toggleFade(isVisible: boolean, index: number) {
	if (index === qualityTVTitles.value.length - 1) hideTitlesFade.value = isVisible;
}

function imageUrl(titlePosterUrl: string | null | undefined) {
	return Vue.filter('Thumbor')({
		url: titlePosterUrl,
		imageType: 'poster',
	} as ThumborOptions);
}

function imbdScore(title: { content: { scoring: { imdbScore?: number | null } } }) {
	return title.content.scoring?.imdbScore?.toFixed(1);
}

return { __sfc: true,props, titleListContainer, tc, t, country, qualityTVProviderId, qualityTVType, isFreeTrialQTV, bannerText, titlesShownCount, handleTitleClick, handleFreeTrialClick, onVisibilityChanged, qualityTVTitles, qualityTVProvider, freeTrialQTVCTA, hideTitlesFade, intersectionOptionsForFade, toggleFade, imageUrl, imbdScore, mrcObservableOptions, HiddenHorizontalScrollbar, ProviderIcon, faPlay }
}

})